(() => {
  const STORAGE_KEY = 'jr_postcode_au';
  const STORAGE_STATE_KEY = 'jr_state_au';
  const STORAGE_RULES_KEY = 'jr_postcode_rules_v1';

  function normalisePostcode(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, '')
      .slice(0, 4);
  }

  function isValidAuPostcode(value) {
    return /^[0-9]{4}$/.test(String(value || ''));
  }

  function postcodeToAuState(postcode) {
    if (!/^[0-9]{4}$/.test(postcode)) return '';
    const n = parseInt(postcode, 10);

    if (n >= 200 && n <= 299) return 'Australian Capital Territory';
    if (n >= 2600 && n <= 2618) return 'Australian Capital Territory';
    if (n >= 2900 && n <= 2920) return 'Australian Capital Territory';

    if (n >= 1000 && n <= 2599) return 'New South Wales';
    if (n >= 2620 && n <= 2899) return 'New South Wales';
    if (n >= 2921 && n <= 2999) return 'New South Wales';

    if (n >= 3000 && n <= 3999) return 'Victoria';
    if (n >= 8000 && n <= 8999) return 'Victoria';

    if (n >= 4000 && n <= 4999) return 'Queensland';
    if (n >= 9000 && n <= 9999) return 'Queensland';

    if (n >= 5000 && n <= 5799) return 'South Australia';
    if (n >= 6000 && n <= 6797) return 'Western Australia';
    if (n >= 7000 && n <= 7799) return 'Tasmania';

    if (n >= 800 && n <= 899) return 'Northern Territory';
    if (n >= 900 && n <= 999) return 'Northern Territory';

    return '';
  }

  function parseRules(raw) {
    const text = String(raw || '').replace(/\r\n/g, '\n');
    const lines = text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'));

    const rules = [];
    for (const line of lines) {
      for (const part of line.split(',').map((p) => p.trim()).filter(Boolean)) {
        // Prefix wildcard: 2*** or 20**
        if (/^[0-9]{1,4}\*+$/.test(part)) {
          rules.push({ type: 'prefix', value: part.replace(/\*+$/, '') });
          continue;
        }
        // Range: 2000-2234
        if (/^[0-9]{4}\s*-\s*[0-9]{4}$/.test(part)) {
          const [a, b] = part.split('-').map((v) => parseInt(v.trim(), 10));
          if (Number.isFinite(a) && Number.isFinite(b)) {
            rules.push({ type: 'range', min: Math.min(a, b), max: Math.max(a, b) });
          }
          continue;
        }
        // Exact: 2000
        if (/^[0-9]{4}$/.test(part)) {
          rules.push({ type: 'exact', value: part });
        }
      }
    }
    return rules;
  }

  function matchesRules(postcode, rules) {
    if (!isValidAuPostcode(postcode)) return false;
    const n = parseInt(postcode, 10);
    for (const rule of rules) {
      if (rule.type === 'exact' && rule.value === postcode) return true;
      if (rule.type === 'range' && n >= rule.min && n <= rule.max) return true;
      if (rule.type === 'prefix' && postcode.startsWith(rule.value)) return true;
    }
    return false;
  }

  function setGlobalMetroFlag(enabled) {
    const root = document.documentElement;
    if (enabled) root.dataset.jrMetroDelivery = '1';
    else delete root.dataset.jrMetroDelivery;
  }

  function emitChange(payload, source) {
    document.dispatchEvent(new CustomEvent('jr:postcode-change', { detail: { ...payload, source } }));
  }

  function initWidget(root) {
    const input = root.querySelector('input');
    const status = root.querySelector('[data-jr-postcode-status]');
    const editBtn = root.querySelector('[data-jr-postcode-edit]');
    const clearBtn = root.querySelector('[data-jr-postcode-clear]');
    const rulesRaw = root.getAttribute('data-free-postcode-rules') || '';
    const rules = parseRules(rulesRaw);
    try {
      if (rulesRaw && rules.length) localStorage.setItem(STORAGE_RULES_KEY, rulesRaw);
    } catch {
      // ignore
    }

    if (!input) return;

    const saved = normalisePostcode(localStorage.getItem(STORAGE_KEY));
    if (saved) input.value = saved;

    let applyTimer = 0;
    const cancelApply = () => {
      if (!applyTimer) return;
      window.clearTimeout(applyTimer);
      applyTimer = 0;
    };

    function renderState(postcode, isMetro) {
      if (!status) return;
      if (!postcode) {
        status.textContent = 'Set your postcode';
        return;
      }
      if (!isValidAuPostcode(postcode)) {
        status.textContent = 'Enter 4 digits';
        return;
      }
      status.textContent = isMetro ? 'Metro free delivery (Aussie Made)' : 'Saved';
    }

    function apply(postcode) {
      const pc = normalisePostcode(postcode);
      input.value = pc;

      if (clearBtn) clearBtn.hidden = !pc;

      if (!pc) {
        root.dataset.jrPostcodeSet = '0';
        if (editBtn) editBtn.textContent = 'Set';
        localStorage.removeItem(STORAGE_KEY);
        setGlobalMetroFlag(false);
        emitChange({ postcode: '', state: '', isMetroFree: false }, 'postcode-widget');
        renderState('', false);
        return;
      }

      const ok = isValidAuPostcode(pc);
      if (!ok) {
        root.dataset.jrPostcodeSet = '0';
        if (editBtn) editBtn.textContent = 'Set';
        setGlobalMetroFlag(false);
        emitChange({ postcode: pc, state: '', isMetroFree: false }, 'postcode-widget');
        renderState(pc, false);
        return;
      }

      root.dataset.jrPostcodeSet = '1';
      if (editBtn) editBtn.textContent = 'Change';
      localStorage.setItem(STORAGE_KEY, pc);
      const inferredState = postcodeToAuState(pc);
      if (inferredState) localStorage.setItem(STORAGE_STATE_KEY, inferredState);

      const metroStateWhitelist = [
        'Australian Capital Territory',
        'New South Wales',
        'Queensland',
        'Victoria',
      ];
      const isEligibleState = inferredState ? metroStateWhitelist.includes(inferredState) : true;
      const isMetroFree = isEligibleState && rules.length ? matchesRules(pc, rules) : false;
      setGlobalMetroFlag(isMetroFree);
      emitChange({ postcode: pc, state: inferredState, isMetroFree }, 'postcode-widget');
      renderState(pc, isMetroFree);
    }

    input.addEventListener('input', () => {
      cancelApply();
      const pc = normalisePostcode(input.value);
      if (pc.length === 4) {
        applyTimer = window.setTimeout(() => apply(pc), 450);
      } else {
        renderState(pc, false);
      }
    });

    input.addEventListener('blur', () => {
      cancelApply();
      apply(input.value);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      cancelApply();
      apply(input.value);
      input.blur();
    });

    input.addEventListener('focus', () => {
      try {
        input.select();
      } catch {
        // ignore
      }
    });

    if (editBtn) {
      editBtn.addEventListener('click', () => {
        cancelApply();
        const isSet = root.dataset.jrPostcodeSet === '1';
        if (!isSet) {
          apply(input.value);
          input.blur();
          return;
        }
        input.focus();
        try {
          input.select();
        } catch {
          // ignore
        }
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        cancelApply();
        apply('');
        input.focus();
      });
    }

    document.addEventListener('jr:postcode-change', (e) => {
      const detail = e?.detail || {};
      if (detail.source === 'postcode-widget') return;
      const postcode = normalisePostcode(detail.postcode);
      if (!postcode) {
        apply('');
        return;
      }
      apply(postcode);
    });

    apply(saved || '');
  }

  function initMobileRelocation(widgets) {
    if (!widgets || !widgets.length) return;

    const widget = widgets[0];
    const originalParent = widget.parentElement;
    const originalNext = widget.nextSibling;
    if (!originalParent) return;

    const mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

    const getDrawerTarget = () => {
      const drawer = document.getElementById('menu-drawer');
      if (!drawer) return null;
      return drawer.querySelector('.menu-drawer__utility-links');
    };

    const moveToDrawer = () => {
      const target = getDrawerTarget();
      if (!target) return;
      if (!target.contains(widget)) {
        target.insertBefore(widget, target.firstChild);
      }
      widget.dataset.jrPostcodePlacement = 'drawer';
    };

    const moveToHeader = () => {
      if (originalParent.contains(widget)) return;
      if (originalNext && originalNext.parentNode === originalParent) {
        originalParent.insertBefore(widget, originalNext);
      } else {
        originalParent.appendChild(widget);
      }
      widget.dataset.jrPostcodePlacement = 'header';
    };

    const applyPlacement = () => {
      const isMobile = mq ? mq.matches : window.innerWidth < 750;
      if (isMobile) moveToDrawer();
      else moveToHeader();
    };

    applyPlacement();

    if (mq && mq.addEventListener) {
      mq.addEventListener('change', applyPlacement);
    } else if (mq && mq.addListener) {
      mq.addListener(applyPlacement);
    }

    // If the drawer is injected later (rare), try again on open.
    document.addEventListener(
      'toggle',
      (e) => {
        if (!mq || !mq.matches) return;
        const details = e.target instanceof HTMLElement ? e.target : null;
        if (!details || details.id !== 'Details-menu-drawer-container') return;
        applyPlacement();
      },
      true
    );
  }

  function init() {
    // Default to hidden until we compute eligibility.
    setGlobalMetroFlag(false);

    const widgets = Array.from(document.querySelectorAll('[data-jr-postcode]'));
    widgets.forEach(initWidget);
    initMobileRelocation(widgets);

    // If there is no widget on the page, still compute eligibility from saved postcode + saved rules.
    if (widgets.length === 0) {
      const saved = normalisePostcode(localStorage.getItem(STORAGE_KEY));
      if (!saved) return;
      const inferredState = postcodeToAuState(saved);
      const metroStateWhitelist = [
        'Australian Capital Territory',
        'New South Wales',
        'Queensland',
        'Victoria',
      ];
      const isEligibleState = inferredState ? metroStateWhitelist.includes(inferredState) : true;
      let rulesRaw = '';
      try {
        rulesRaw = localStorage.getItem(STORAGE_RULES_KEY) || '';
      } catch {
        rulesRaw = '';
      }
      const rules = parseRules(rulesRaw);
      const isMetroFree = isEligibleState && rules.length ? matchesRules(saved, rules) : false;
      setGlobalMetroFlag(isMetroFree);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
